import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { IDetailTour } from "@interfaces";
import { RootState } from ".";
import { toursApi } from "@api";
interface IFavoriteToursState {
    tours: IDetailTour[];
    loading: boolean;
    error: string | null;
}

const initialState: IFavoriteToursState = {
    tours: [],
    loading: false,
    error: null,
};

// Fetch favorites list
export const fetchFavoriteTours = createAsyncThunk<IDetailTour[], void, { rejectValue: string }>(
    "favoriteTours/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const res = await toursApi.getFavouriteTour();
            return res?.data.data as IDetailTour[];
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    },
);

// Update favorite (like/unlike) and return method for reducer
export const updateFavorite = createAsyncThunk<
    { tours: IDetailTour[]; method: number; productId: string | number },
    { productId: string | number; method: number },
    { rejectValue: string }
>("favoriteTours/update", async ({ productId, method }, { rejectWithValue }) => {
    try {
        if (method === 1) {
            await toursApi.addToFavourite(productId);
        } else {
            await toursApi.deleteFavourite(productId);
        }
        const res = await toursApi.getFavouriteTour();
        return { tours: res?.data.data as IDetailTour[], method, productId };
    } catch (err: any) {
        return rejectWithValue(err.message);
    }
});

const favoriteToursSlice = createSlice({
    name: "favoriteTours",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchFavoriteTours.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchFavoriteTours.fulfilled,
                (state, action: PayloadAction<IDetailTour[]>) => {
                    state.loading = false;
                    state.tours = action.payload;
                },
            )
            .addCase(fetchFavoriteTours.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to fetch favorites";
            })

            .addCase(updateFavorite.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                updateFavorite.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        tours: IDetailTour[];
                        method: number;
                        productId: string | number;
                    }>,
                ) => {
                    state.loading = false;
                    const { tours, method, productId } = action.payload;
                    if (method === 0) {
                        state.tours = tours.filter((tour) => {
                            const id = tour.tdms_product_id;
                            return id?.toString() !== productId.toString();
                        });
                    } else {
                        state.tours = action.payload.tours;
                    }
                },
            )
            .addCase(updateFavorite.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to update favorite";
            });
    },
});

export const selectFavoriteTours = (state: RootState) => state.favoriteTours;
export default favoriteToursSlice.reducer;
